// src/pages/offlineSales/terminal/components/PaymentModal.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Plus, Trash2, Banknote, CreditCard, Delete, CheckCircle2, Lock } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import { PaymentMethodIcon, getPaymentMethodIconUrl } from '../../../../utils/paymentMethodIcons';
import styles from '../OfflineSalesTerminal.module.css';

// Static fallback list if no methods are loaded from the API yet
const FALLBACK_PAYMENT_METHODS = [
  { key: 'cash', name: 'Cash', feeType: 'none', feeFlat: 0, feePercentage: 0, feeChargedToCustomer: false },
  { key: 'card', name: 'Card', feeType: 'none', feeFlat: 0, feePercentage: 0, feeChargedToCustomer: false },
  { key: 'bank_transfer', name: 'Bank Transfer', feeType: 'none', feeFlat: 0, feePercentage: 0, feeChargedToCustomer: false },
  { key: 'voucher', name: 'Voucher', feeType: 'none', feeFlat: 0, feePercentage: 0, feeChargedToCustomer: false },
];

const CASH_SHORTCUTS = [10, 20, 100, 500, 1000, 2000, 5000];
const NUMPAD_KEYS = ['7','8','9','4','5','6','1','2','3','C','0','⌫'];

// API methods carry `label`; the fallback list uses `name`
const methodLabel = (method) => method ? (method.label ?? method.name ?? method.key) : '';

const calcFee = (method, amount) => {
  if (!method || method.feeType === 'none') return 0;
  const flat = parseFloat(method.feeFlat) || 0;
  const pct = parseFloat(method.feePercentage) || 0;
  if (method.feeType === 'flat') return flat;
  if (method.feeType === 'percentage') return amount * pct / 100;
  if (method.feeType === 'flat_and_percentage') return flat + (amount * pct / 100);
  return 0;
};

const describeFee = (method) => {
  if (!method || method.feeType === 'none') return null;
  const flat = parseFloat(method.feeFlat) || 0;
  const pct = parseFloat(method.feePercentage) || 0;
  if (method.feeType === 'flat') return `Fee: Rs. ${flat.toFixed(2)}`;
  if (method.feeType === 'percentage') return `Fee: ${pct}%`;
  if (method.feeType === 'flat_and_percentage') return `Fee: Rs. ${flat.toFixed(2)} + ${pct}%`;
  return null;
};

// existingPayments: payment records already on a store pickup order being completed. Verified ones
// are locked; unverified ones can be removed, or are verified automatically when the sale completes.
const PaymentModal = ({
  grandTotal: orderGrandTotal, paymentMethods: methodsProp, onConfirm, onClose, submitting, initialMethodKey = null,
  existingPayments = [], onRemoveExistingPayment, allowCredit = true,
}) => {
  const { t, formatCurrency } = useLocalization();

  // New payments only need to cover what the existing payments don't
  const existingPaid = existingPayments.reduce((s, p) => s + (parseFloat(p.paymentAmount) || 0), 0);
  const grandTotal = Math.max(0, orderGrandTotal - existingPaid);

  // Build the active methods list — prefer API data, fall back to static list
  const availableMethods = useMemo(() => {
    if (methodsProp && methodsProp.length > 0) {
      return methodsProp.filter(m => m.isActive !== false);
    }
    return FALLBACK_PAYMENT_METHODS;
  }, [methodsProp]);

  // Method lookup map keyed by method.key
  const methodMap = useMemo(() => {
    const map = {};
    availableMethods.forEach(m => { map[m.key] = m; });
    return map;
  }, [availableMethods]);

  // Preselect the requested method (e.g. Cash button) when it's available, else the first one
  const defaultMethodKey = initialMethodKey && methodMap[initialMethodKey]
    ? initialMethodKey
    : (availableMethods.length > 0 ? availableMethods[0].key : 'cash');

  // Each row: { method (key string), value (string — what numpad enters), reference }
  // No new payment row when existing payments already cover the order
  const newPaymentRow = (value, method = defaultMethodKey) => ({ method, value: String(value), reference: '' });
  const [payments, setPayments] = useState(grandTotal > 0 ? [newPaymentRow(grandTotal)] : []);
  const [activeIdx, setActiveIdx] = useState(0);
  const [status, setStatus] = useState('completed');

  // Removing an unverified existing payment raises the balance — keep a lone payment row in step
  const prevGrandTotalRef = useRef(grandTotal);
  useEffect(() => {
    if (prevGrandTotalRef.current === grandTotal) return;
    prevGrandTotalRef.current = grandTotal;
    setPayments(prev => {
      if (grandTotal <= 0) return [];
      if (prev.length === 0) return [newPaymentRow(grandTotal)];
      return prev.length === 1 ? [{ ...prev[0], value: String(grandTotal) }] : prev;
    });
    setActiveIdx(0);
  }, [grandTotal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute effective amounts sequentially so split payments add up correctly.
  // Also compute per-row fee info.
  const computed = useMemo(() => {
    let leftover = grandTotal;
    return payments.map(p => {
      const entered = parseFloat(p.value) || 0;
      const effective = Math.min(entered, Math.max(0, leftover));
      leftover = Math.max(0, leftover - effective);
      const methodObj = methodMap[p.method];
      const feeAmount = calcFee(methodObj, effective);
      const feeChargedToCustomer = methodObj ? Boolean(methodObj.feeChargedToCustomer) : false;
      const change = p.method === 'cash' ? Math.max(0, entered - effective - (feeChargedToCustomer ? feeAmount : 0)) : 0;
      return { effective, change, feeAmount, feeChargedToCustomer };
    });
  }, [payments, grandTotal, methodMap]);

  const totalCustomerFees = computed.reduce((s, c) => s + (c.feeChargedToCustomer ? c.feeAmount : 0), 0);
  const totalPaid = computed.reduce((s, c) => s + c.effective, 0);
  const totalDue = grandTotal + totalCustomerFees;
  const remaining = Math.max(0, totalDue - totalPaid);
  const cashChange = computed.reduce((s, c) => s + c.change, 0);

  const handleNumpad = (key) => {
    setPayments(prev => prev.map((p, i) => {
      if (i !== activeIdx) return p;
      if (key === 'C') return { ...p, value: '' };
      if (key === '⌫') return { ...p, value: p.value.slice(0, -1) };
      return { ...p, value: p.value + key };
    }));
  };

  const handleShortcut = (amount) => {
    setPayments(prev => prev.map((p, i) =>
      i === activeIdx ? { ...p, value: String(amount) } : p
    ));
  };

  const updateMethod = (idx, methodKey) => {
    if (payments.length === 0) {
      if (remaining <= 0) return;
      setPayments([newPaymentRow(remaining, methodKey)]);
      setActiveIdx(0);
      return;
    }
    setPayments(prev => prev.map((p, i) => i === idx ? { ...p, method: methodKey } : p));
    setActiveIdx(idx);
  };

  const updateReference = (idx, val) => {
    setPayments(prev => prev.map((p, i) => i === idx ? { ...p, reference: val } : p));
  };

  const addPayment = () => {
    const idx = payments.length;
    // Pick second available method if possible for split payments
    const nextMethodKey = availableMethods.length > 1 && payments.length > 0
      ? availableMethods.find(m => m.key !== payments[payments.length - 1].method)?.key || availableMethods[1].key
      : defaultMethodKey;
    setPayments(prev => [...prev, { method: nextMethodKey, value: String(remaining), reference: '' }]);
    setActiveIdx(idx);
  };

  const removePayment = (idx) => {
    setPayments(prev => prev.filter((_, i) => i !== idx));
    setActiveIdx(Math.max(0, activeIdx - (idx <= activeIdx ? 1 : 0)));
  };

  const handleConfirm = () => {
    const details = payments
      .map((p, i) => {
        const { effective, change } = computed[i];
        if (effective <= 0) return null;
        const obj = { paymentMethodKey: p.method, amount: effective };
        if (p.method === 'cash') {
          obj.cashReceived = parseFloat(p.value) || effective;
          obj.changeGiven = change;
        }
        if (p.reference) obj.reference = p.reference;
        return obj;
      })
      .filter(Boolean);
    onConfirm(details, status);
  };

  const activePayment = payments[activeIdx];
  const activeComputed = computed[activeIdx];
  const activeMethodObj = methodMap[activePayment?.method];
  const canComplete = remaining <= 0 || status === 'credit';

  // Keep a stable ref to handleConfirm so the keyboard effect can call it
  const handleConfirmRef = useRef(handleConfirm);
  useEffect(() => { handleConfirmRef.current = handleConfirm; });

  // Keyboard input — digits feed the active payment, Backspace/Delete clear, Enter confirms
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      // Let the browser handle normal text input fields / dropdowns
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        setPayments(prev => prev.map((p, i) =>
          i !== activeIdx ? p : { ...p, value: p.value + e.key }
        ));
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setPayments(prev => prev.map((p, i) =>
          i !== activeIdx ? p : { ...p, value: p.value.slice(0, -1) }
        ));
      } else if (e.key === 'Delete') {
        e.preventDefault();
        setPayments(prev => prev.map((p, i) =>
          i !== activeIdx ? p : { ...p, value: '' }
        ));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (canComplete && !submitting) handleConfirmRef.current();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [activeIdx, canComplete, submitting, onClose]);

  const clearAllPayments = () => {
    setPayments(grandTotal > 0 ? [newPaymentRow('')] : []);
    setActiveIdx(0);
  };

  const renderMethodIcon = (method, size = 'lg') => {
    if (method && getPaymentMethodIconUrl(method.iconFileName)) {
      return (
        <PaymentMethodIcon
          iconFileName={method.iconFileName}
          label={methodLabel(method)}
          width={size === 'lg' ? 64 : 32}
        />
      );
    }
    const Icon = method?.key === 'cash' ? Banknote : CreditCard;
    return <Icon size={size === 'lg' ? 28 : 18} />;
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.paymentModal}`} onClick={e => e.stopPropagation()}>
        <div className={styles.pmHeader}>
          <h3>{t('pos.payment')}</h3>
          <button onClick={onClose} className={styles.modalClose}><X size={20} /></button>
        </div>

        <div className={styles.pmBody}>
          {/* Amount due */}
          <div className={styles.pmDue}>
            <div>
              <span>{t('pos.amountDue')}</span>
              {totalCustomerFees > 0 && (
                <small>incl. {formatCurrency(totalCustomerFees)} payment fees</small>
              )}
              {existingPaid > 0 && (
                <small>{formatCurrency(orderGrandTotal)} total, {formatCurrency(existingPaid)} already paid</small>
              )}
            </div>
            <strong>{formatCurrency(totalDue)}</strong>
          </div>

          {/* 1. Payment method — applies to the active payment row */}
          <div className={styles.pmSectionTitle}>1. Select Payment Method</div>
          <div className={styles.pmMethodGrid}>
            {availableMethods.map(method => (
              <button
                key={method.key}
                type="button"
                className={`${styles.pmMethodTile} ${activePayment?.method === method.key ? styles.pmMethodTileActive : ''}`}
                onClick={() => updateMethod(activeIdx, method.key)}
                title={describeFee(method) || methodLabel(method)}
              >
                <span className={styles.pmMethodIcon}>{renderMethodIcon(method)}</span>
                <span className={styles.pmMethodName}>{methodLabel(method)}</span>
              </button>
            ))}
          </div>

          {/* 2. Amount */}
          <div className={styles.pmSectionTitle}>2. Enter Payment Amount</div>
          <div className={styles.pmAmountLayout}>
            <div className={styles.pmEntry}>
              <div className={styles.pmAmountDisplay}>
                <span>{activePayment ? `${methodLabel(activeMethodObj) || activePayment.method} Payment` : 'No payment due'}</span>
                <strong>{formatCurrency(parseFloat(activePayment?.value) || 0)}</strong>
              </div>

              <div className={styles.pmShortcuts}>
                {CASH_SHORTCUTS.map(amt => (
                  <button
                    key={amt}
                    className={styles.pmShortcutBtn}
                    onClick={() => handleShortcut(amt)}
                  >
                    {amt.toLocaleString()}
                  </button>
                ))}
              </div>

              <div className={styles.pmNumpad}>
                {NUMPAD_KEYS.map(k => (
                  <button
                    key={k}
                    className={`${styles.pmNumpadKey} ${k === 'C' ? styles.pmNumpadClear : ''}`}
                    onClick={() => handleNumpad(k)}
                  >
                    {k === '⌫' ? <Delete size={22} /> : k}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.pmSelected}>
              <div className={styles.pmSelectedHeader}>
                <span>Selected Payments</span>
                <button className={styles.pmClearAll} onClick={clearAllPayments}>
                  <Trash2 size={14} /> Clear All
                </button>
              </div>

              <div className={styles.pmSelectedList}>
                {existingPayments.map(record => {
                  const rowMethod = methodMap[record.paymentMethodKey];
                  return (
                    <div
                      key={`existing-${record.id}`}
                      className={`${styles.pmSelectedRow} ${styles.pmExistingRow} ${record.isVerified ? '' : styles.pmExistingRowUnverified}`}
                    >
                      <span className={styles.pmSelectedIcon}>{renderMethodIcon(rowMethod, 'sm')}</span>
                      <div className={styles.pmSelectedInfo}>
                        <span className={styles.pmSelectedName}>
                          {record.paymentMethodLabel || methodLabel(rowMethod) || record.paymentMethodKey}
                          {record.reference ? ` · ${record.reference}` : ''}
                        </span>
                        <span className={styles.pmSelectedAmount}>{formatCurrency(record.paymentAmount)}</span>
                      </div>
                      {record.isVerified ? (
                        <span className={styles.pmExistingLock} title="Verified payment"><Lock size={16} /></span>
                      ) : (
                        <button
                          onClick={e => { e.stopPropagation(); onRemoveExistingPayment?.(record.id); }}
                          className={styles.pmRowRemove}
                          title="Remove unverified payment"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}

                {payments.map((p, idx) => {
                  const rowComputed = computed[idx];
                  const rowMethod = methodMap[p.method];
                  const feeDesc = rowComputed && rowComputed.feeAmount > 0 ? describeFee(rowMethod) : null;
                  return (
                    <div
                      key={idx}
                      className={`${styles.pmSelectedRow} ${activeIdx === idx ? styles.pmSelectedRowActive : ''}`}
                      onClick={() => setActiveIdx(idx)}
                    >
                      <span className={styles.pmSelectedIcon}>{renderMethodIcon(rowMethod, 'sm')}</span>
                      <div className={styles.pmSelectedInfo}>
                        <span className={styles.pmSelectedName}>{methodLabel(rowMethod) || p.method}</span>
                        <span className={styles.pmSelectedAmount}>{formatCurrency(parseFloat(p.value) || 0)}</span>
                        {feeDesc && rowComputed.feeChargedToCustomer && (
                          <span className={styles.paymentFeeNote}>+{formatCurrency(rowComputed.feeAmount)} fee</span>
                        )}
                        {feeDesc && !rowComputed.feeChargedToCustomer && (
                          <span className={styles.paymentFeeNoteAbsorbed}>{feeDesc} (absorbed)</span>
                        )}
                      </div>
                      {payments.length > 1 && (
                        <button
                          onClick={e => { e.stopPropagation(); removePayment(idx); }}
                          className={styles.pmRowRemove}
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  );
                })}

                {remaining > 0 && (
                  <button onClick={addPayment} className={styles.pmAddPayment}>
                    <span className={styles.pmAddIcon}><Plus size={16} /></span>
                    {t('pos.addPayment')}
                  </button>
                )}
              </div>

              <div className={styles.pmTotals}>
                <div className={styles.pmTotalRow}>
                  <span>{t('pos.remaining')}</span>
                  <strong className={remaining > 0 ? styles.pmAmountDue : styles.pmAmountOk}>
                    {formatCurrency(remaining)}
                  </strong>
                </div>
                {cashChange > 0 && (
                  <div className={styles.pmTotalRow}>
                    <span>{t('pos.change')}</span>
                    <strong className={styles.pmAmountOk}>{formatCurrency(cashChange)}</strong>
                  </div>
                )}
                {remaining > 0 && allowCredit && (
                  <label className={styles.pmCredit}>
                    <input
                      type="checkbox"
                      checked={status === 'credit'}
                      onChange={e => setStatus(e.target.checked ? 'credit' : 'completed')}
                    />
                    {t('pos.creditSale')}
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Reference for the active payment row */}
          <div className={styles.pmReference}>
            <label>Reference (optional)</label>
            <input
              type="text"
              value={activePayment?.reference || ''}
              onChange={e => updateReference(activeIdx, e.target.value)}
              placeholder="Enter reference no. / transaction ID / notes..."
            />
          </div>
        </div>

        <div className={styles.pmFooter}>
          <button onClick={onClose} className={styles.pmCancelBtn}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !canComplete}
            className={styles.pmCompleteBtn}
          >
            <CheckCircle2 size={22} />
            {submitting ? '...' : t('pos.completeSale')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
